import { request } from '@pb/utils'
import { merge, omit } from 'ramda'

import {
  PostProceedBody,
  PostProceedResponse,
} from 'features/checkout/duck/operations/postProceed/prepareProceedBody/paymentProviders'
import {
  DeliveryMode,
  PhoneNumberData,
  DeliveryRequestData,
  Address,
  CustomerDetails,
  Coordinates,
  PickupPlace,
  ValidatePhone,
  CheckoutSettings,
  PaymentMode,
  Prepare,
  DeliveryAddressFormData,
  AddressResponseData,
  GiftCardInfo,
  AddressValidateResponseData,
  V2Consignment,
  DeliveryPatchResponse,
  Context,
  PatchPaymentResponseData,
  AuthTokenResponse,
  AuthTokenResponseNormalized,
  KlarnaSessionResponse,
} from 'features/checkout/types'

import { API_VERSION } from './constants'

export const postDeliveryModes = (
  address: Address,
  consignmentData?: V2Consignment[],
): Promise<DeliveryMode[]> => {
  return request
    .post('checkout/delivery-modes', {
      data: {
        deliveryModesRequestList: [
          {
            ...(consignmentData && {
              consignmentList: consignmentData,
            }),
            address,
          },
        ],
      },
      apiVersion: API_VERSION.TWO,
    })
    .then((res) => res.data[0].availableDeliveryModes)
}
/**
 * get the Klarna session body for Klarna.Payments.authorize call
 */
export const getKlarnaSession = (dateOfBirth?: string): Promise<KlarnaSessionResponse> =>
  request
    .get('checkout/omnicredit/omnicredit-session', { params: { dateOfBirth } })
    .then((res) => res.data)

/**
 * tell backend that klarna failed
 */
export const postKlarnaError = (paymentMode: string): Promise<Object> =>
  request
    .post('checkout/omnicredit/omnicredit-error', {
      data: {
        paymentMode,
      },
    })
    .then((res) => res.data)

/**
 * retrieve Klarna auth token from server side authorisation callback
 */
export const getKlarnaAuthToken = (sessionId: string): Promise<AuthTokenResponseNormalized> =>
  request
    .get('checkout/omnicredit/get-auth-token', { params: { sessionId } })
    .then((res) => res.data)
    .then((data: AuthTokenResponse) => ({ serverSideAuthorizationToken: data.authorisationToken }))

/**
 * fetchHelpParagraphComponent
 * @returns {json}
 */

export const fetchHelpParagraphComponent = async () => {
  const res = await request.get('helpParagraphComponent', {
    type: 'adobe',
  })
  return res.data
}
/**
 * make a post request to checkout/prepare with customerType
 *
 * @param {string} customerType
 */
export const postPrepare = (customerType: string): Promise<Prepare> =>
  request
    .post('checkout/prepare?context=ALL', {
      data: {
        customerType,
      },
      apiVersion: API_VERSION.TWO,
    })
    .then((res) => res.data)

/**
 * make a patch request to select payment method
 *
 * @param {Object} { paymentId: String }
 * returns {Object} context
 */

export const patchCheckoutPayment = (paymentId: string): Promise<PatchPaymentResponseData> =>
  request
    .patch('checkout/payment', {
      data: {
        paymentId,
      },
      apiVersion: API_VERSION.TWO,
    })
    .then((res) => res.data)

export const fetchSettings = (): Promise<CheckoutSettings> =>
  request.get('checkout/settings', { apiVersion: API_VERSION.TWO }).then((res) => res.data)

/**
 * make fetch request to context
 *
 * @returns {Object} context
 */
export const fetchContext = (): Promise<Context> =>
  request
    .get('checkout/context', {
      apiVersion: API_VERSION.TWO,
    })
    .then((res) => res.data)

export const putCustomerDetails = async (data: {
  address: Address
  vatNumber?: string
}): Promise<CustomerDetails> => {
  const finalData = merge({ ...data.address }, omit(['address'], data))
  return (
    request
      // TODO: Remove context specific query once BE updates customer API [COWEB-2742]
      .put('checkout/customer?context=CART,CUSTOMER,TOTALS', {
        data: finalData,
        apiVersion: API_VERSION.TWO,
      })
      .then((res) => res.data as CustomerDetails)
  )
}

export const postCustomerDetails = (data: {
  address: Address
  email: string
  vatNumber?: string
}): Promise<CustomerDetails> => {
  const finalData = merge({ ...data.address }, omit(['address'], data))
  return (
    request
      // TODO: Remove context specific query once BE updates customer API [COWEB-2742]
      .post('checkout/customer?context=CART,CUSTOMER,TOTALS', {
        data: finalData,
        apiVersion: API_VERSION.TWO,
      })
      .then((res) => res.data as CustomerDetails)
  )
}

/**
 *
 * make a get request to checkout/delivery/pickupPlaces
 *
 * @param {postalCode} String :: required by backend
 * @param {deliveryModeCodes} String[] :: each with return an array of possible delivery options
 */
export const fetchPickupPlacesByPostalCode: (
  postalCode: string,
  deliveryModeCodes?: string[],
) => Promise<PickupPlace[]> = async (postalCode, deliveryModeCodes = []) => {
  // TODO: change mock and this logic from &deliveryModeCode= to semicolon seperated, ( need to confirm this because the way I have implemented it now is how it works in o2t environment )
  const deliveryModeCodesQuery = deliveryModeCodes
    .map((code) => `&deliveryModeCode=${code}`)
    .join('')

  const { data } = await request.get(
    `checkout/delivery/pickupPlaces?postalCode=${postalCode}${deliveryModeCodesQuery}`,
    { apiVersion: API_VERSION.TWO },
  )

  return data
}

/**
 *
 * make a get request to checkout/delivery/pickupPlaces
 *
 * @param {postalCode} String :: required by backend
 * @param {city} String :: required by backend
 * @param {province} String :: required by backend
 * @param {deliveryModeCodes} String[] :: each with return an array of possible delivery options
 */
export const fetchPickupPlacesByAddress: (
  postalCode: string,
  city: string,
  province: string,
  deliveryModeCodes?: string[],
) => Promise<PickupPlace[]> = async (postalCode, city, province, deliveryModeCodes = []) => {
  // TODO: change mock and this logic from &deliveryModeCode= to semicolon seperated, ( need to confirm this because the way I have implemented it now is how it works in o2t environment )
  const deliveryModeCodesQuery = deliveryModeCodes
    .map((code) => `&deliveryModeCode=${code}`)
    .join('')

  const { data } = await request.get(
    `checkout/delivery/pickupPlaces?postalCode=${postalCode}&city=${city}&province=${province}${deliveryModeCodesQuery}`,
    { apiVersion: API_VERSION.TWO },
  )

  return data
}

/**
 *
 * make a get request to checkout/delivery/pickupPlaces
 *
 * @param {coordinates} Coordinates :: required by backend
 * @param {deliveryModeCodes} String[] :: each with return an array of possible delivery options
 */
export const fetchPickupPlacesByCoordinates: (
  coordinates: Coordinates,
  deliveryModeCodes?: string[],
) => Promise<PickupPlace[]> = async ({ lat, lng }, deliveryModeCodes = []) => {
  // TODO: change mock and this logic from &deliveryModeCode= to semicolon seperated, ( need to confirm this because the way I have implemented it now is how it works in o2t environment )
  const deliveryModeCodesQuery = deliveryModeCodes
    .map((code) => `&deliveryModeCode=${code}`)
    .join('')

  const { data } = await request.get(
    `checkout/delivery/pickupPlaces?latitude=${lat}&longitude=${lng}${deliveryModeCodesQuery}`,
    { apiVersion: API_VERSION.TWO },
  )

  return data
}
/**
 * make a POST request to checkout/addresses with new address details
 *
 * @param {object} addressDetails New Address Details
 */
export const postNewAddress = (
  addressDetails: DeliveryAddressFormData | Address,
): Promise<AddressResponseData> =>
  request
    .post('checkout/address', {
      data: {
        ...addressDetails,
      },
      apiVersion: API_VERSION.TWO,
    })
    .then((res) => res.data)

/**
 * make a POST request to /checkout/address-validate
 * @param addressDetails New Address Details
 *
 */

export const addressValidate = (
  addressDetails: DeliveryAddressFormData,
): Promise<AddressValidateResponseData> => {
  return request
    .post('checkout/address-validate', {
      data: {
        ...addressDetails,
      },
      apiVersion: API_VERSION.TWO,
    })
    .then((res) => res.data)
}

/**
 * make a PUT request to checkout/address/<addressID> with new address details
 *
 * @param {string} addressID Edited address ID
 * @param {object} addressDetails New Address Details
 */
export const updateAddress = (
  addressID: string,
  addressDetails: DeliveryAddressFormData,
): Promise<AddressResponseData> =>
  request
    .put(`checkout/address/${addressID}`, {
      data: {
        ...addressDetails,
      },
      apiVersion: API_VERSION.TWO,
    })
    .then((res) => res.data)

export const patchDelivery = (
  deliveryRequestData: DeliveryRequestData,
): Promise<DeliveryPatchResponse> =>
  request
    .patch('checkout/delivery', {
      data: { deliveryRequestDatas: [deliveryRequestData] },
      apiVersion: API_VERSION.TWO,
    })
    .then((res) => res.data)

export const postProceedCheckout = (data: PostProceedBody): Promise<PostProceedResponse> =>
  request.post(`checkout/proceed`, { data, apiVersion: API_VERSION.TWO }).then((res) => res.data)

export const postAddGiftCard = (data: GiftCardInfo, inCart?: boolean) =>
  request
    .post(!inCart ? 'checkout/giftcard?context=PAYMENT,TOTALS' : 'checkout/giftcard', {
      data,
      apiVersion: API_VERSION.TWO,
    })
    .then((res) => res.data)

export const deleteGiftCard = (cardNumber: string, inCart?: boolean) =>
  request
    .delete(
      !inCart
        ? `checkout/giftcard/${cardNumber}?context=PAYMENT,TOTALS`
        : `checkout/giftcard/${cardNumber}`,
      { apiVersion: API_VERSION.TWO },
    )
    .then((res) => res.data)

export const postConfirmCheckout = (data) =>
  request
    .post('checkout/confirm', {
      data,
      apiVersion: API_VERSION.TWO,
    })
    .then((res) => res.data)

export const deleteCreditCard = (cardId) =>
  request.delete(`payments/${cardId}`).then((res) => res.data)

export const validatePhone = (phoneNumberData: PhoneNumberData): Promise<ValidatePhone> =>
  request.post(`validatePhone/`, { data: phoneNumberData }).then((res) => res.data)

export const validateExistingUser = (email: string): Promise<{ valid: boolean }> =>
  request.post('userExistence', { data: { email } }).then((res) => ({ valid: res.data }))

export const validateVat = (data: {
  vatNumber: string
  countryIso: string
}): Promise<{ valid: boolean }> =>
  request.post(`checkout/validate-vat`, { data }).then((res) => res.data)

export const loadCities = (province: string): Promise<any> =>
  request.get(`settings/city/${province}`).then((res) => res.data)

export const loadDistricts = (city: string): Promise<any> =>
  request.get(`settings/district/${city}`).then((res) => res.data)

export const fetchPostalCode = (district: string): Promise<string> =>
  request.get(`settings/postalcode/${district}`).then((res) => res.data)

export const fetchPaymentModes = (): Promise<{ paymentModeList: PaymentMode[] }> =>
  request
    .post(`checkout/payment-modes`, {
      data: {},
      apiVersion: API_VERSION.TWO,
    })
    .then((res) => res.data)

export const editQuantityOfItem = (variantCode: string, newQuantity: number): Promise<Prepare> =>
  request
    .put(`/checkout/quantity/${variantCode}/${newQuantity}?context=ALL`, {
      apiVersion: API_VERSION.TWO,
    })
    .then((res) => res.data)
